import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LandingNav } from '../components/Nav';
import SiteFooter from '../components/SiteFooter';
import { applyBlogPostSocialMeta } from './applyBlogPostSocialMeta';

const TOKENS = {
  bg: '#fffcf9',
  text: '#111',
  body: '#7a6b63',
  pink: '#d4537e',
  pinkD: '#993556',
  rule: '#e8e0d8',
};

const BLOG_POST_LAYOUT_CSS = `
.blog-article-page { background: ${TOKENS.bg}; min-height: 100vh; color: ${TOKENS.text}; }
.blog-article-page .blog-article-inner {
  max-width: 720px;
  margin: 0 auto;
  padding: 40px 24px 80px;
  box-sizing: border-box;
}
.blog-article-page .blog-article-back {
  display: inline-block;
  margin-bottom: 28px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${TOKENS.pinkD};
  text-decoration: none;
  border-bottom: 1.5px solid ${TOKENS.pink};
  padding-bottom: 4px;
}
.blog-article-page .blog-article-title {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(32px, 4vw, 40px);
  font-weight: 700;
  letter-spacing: -0.5px;
  line-height: 1.15;
  margin: 0 0 12px;
  color: ${TOKENS.text};
}
.blog-article-page .article-byline {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 8px 0 40px;
}
.blog-article-page .article-byline__avatar-link { display: block; flex-shrink: 0; }
.blog-article-page .article-byline__avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  object-position: center top;
  border: 2px solid #fff;
  box-shadow: 0 2px 8px rgba(114, 36, 62, 0.10);
}
.blog-article-page .article-byline__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.blog-article-page .article-byline__name {
  font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #111;
  text-decoration: none;
}
.blog-article-page .article-byline__name:hover { color: ${TOKENS.pinkD}; }
.blog-article-page .article-byline__role {
  font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
  font-size: 13px;
  color: ${TOKENS.body};
}
.blog-article-page .blog-article-body::after {
  content: '';
  display: table;
  clear: both;
}
.blog-article-page .article-pull-image {
  float: right;
  width: 280px;
  margin: 8px 0 24px 32px;
  shape-outside: margin-box;
}
.blog-article-page .article-pull-image::after {
  content: '';
  display: table;
  clear: both;
}
.blog-article-page .article-pull-image img {
  width: 100%;
  height: auto;
  aspect-ratio: 4 / 5;
  object-fit: cover;
  object-position: center top;
  border-radius: 6px;
  box-shadow: 0 16px 32px -16px rgba(114, 36, 62, 0.18);
  display: block;
}
.blog-article-page .article-pull-image figcaption {
  margin-top: 10px;
  font-family: 'Playfair Display', Georgia, serif;
  font-style: italic;
  font-size: 13px;
  line-height: 1.45;
  font-weight: 400;
  color: #7a6b63;
  text-align: left;
}
@media (max-width: 720px) {
  .blog-article-page .article-pull-image {
    float: none;
    width: 100%;
    max-width: 360px;
    margin: 24px auto 32px;
  }
  .blog-article-page .article-pull-image figcaption {
    text-align: center;
  }
}
.blog-article-page .blog-article-body p {
  font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.75;
  color: ${TOKENS.body};
  margin: 0 0 1.1em;
}
.blog-article-page .blog-article-body > p:last-child {
  margin-bottom: 0;
}
.blog-article-page .blog-pull-quote {
  float: right;
  clear: right;
  width: 240px;
  margin: 4px 0 20px 32px;
  border-top: 2px solid rgb(212, 83, 126);
  padding: 14px 0 0;
}
.blog-article-page .blog-pull-quote--left {
  float: left;
  clear: left;
  margin: 4px 32px 20px 0;
}
.blog-article-page .blog-pull-quote p {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 17px;
  font-style: italic;
  font-weight: 400;
  color: rgb(153, 53, 86);
  line-height: 1.6;
  margin: 0;
}
.blog-article-page .blog-pull-quote-wrap::after {
  content: '';
  display: table;
  clear: both;
}
@media (max-width: 720px) {
  .blog-article-page .blog-pull-quote {
    float: none;
    width: 100%;
    max-width: 360px;
    margin: 24px auto 24px;
  }
}
.blog-article-page .blog-article-em {
  font-family: 'Playfair Display', Georgia, serif;
  font-style: italic;
  font-weight: 400;
  color: ${TOKENS.body};
}
.blog-article-page .blog-article-rule {
  border: none;
  border-top: 1px solid ${TOKENS.rule};
  margin: 32px 0 36px;
  clear: both;
}
.blog-article-page .blog-article-signature {
  margin-top: 2em;
  font-family: 'Playfair Display', Georgia, serif;
  font-style: italic;
  font-weight: 400;
  color: ${TOKENS.body};
  clear: both;
}
.blog-article-page .author-bio {
  margin: 28px 0 0;
  padding: 36px 40px;
  background: #fbeaf0;
  border-left: 4px solid ${TOKENS.pink};
  border-radius: 4px 12px 12px 4px;
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 32px;
  align-items: start;
  clear: both;
}
.blog-article-page .author-bio__photo-link { display: block; flex-shrink: 0; }
.blog-article-page .author-bio__photo {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  object-position: center top;
  border: 3px solid #fff;
  box-shadow: 0 4px 16px rgba(114, 36, 62, 0.15);
}
.blog-article-page .author-bio__content { display: flex; flex-direction: column; gap: 4px; }
.blog-article-page .author-bio__eyebrow {
  font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${TOKENS.pinkD};
  margin-bottom: 4px;
}
.blog-article-page .author-bio__name {
  font-family: 'Playfair Display', Georgia, serif;
  font-weight: 700;
  font-size: 22px;
  color: ${TOKENS.text};
  letter-spacing: -0.5px;
  margin: 0;
}
.blog-article-page .author-bio__name a { color: inherit; text-decoration: none; }
.blog-article-page .author-bio__name a:hover { color: ${TOKENS.pinkD}; }
.blog-article-page .author-bio__role {
  font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
  font-size: 13px;
  color: ${TOKENS.body};
  margin-bottom: 14px;
}
.blog-article-page .author-bio__text {
  font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
  font-size: 15px;
  line-height: 1.65;
  color: #72243e;
  margin: 0 0 16px;
}
.blog-article-page .author-bio__text--final {
  margin-bottom: 0;
}
.blog-article-page .author-bio__link {
  font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${TOKENS.pinkD};
  text-decoration: none;
  border-bottom: 1.5px solid ${TOKENS.pink};
  padding-bottom: 4px;
  align-self: flex-start;
}
.blog-article-page .author-bio__link:hover { color: #72243e; }
@media (max-width: 640px) {
  .blog-article-page .author-bio {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 28px 24px;
  }
  .blog-article-page .author-bio__photo {
    width: 96px;
    height: 96px;
  }
}
`;

function renderParagraphSegments(segments, keyPrefix) {
  if (!Array.isArray(segments)) return null;
  return segments.map((s, j) =>
    s.italic ? (
      <em key={`${keyPrefix}-${j}`} className="blog-article-em">
        {s.text}
      </em>
    ) : (
      <span key={`${keyPrefix}-${j}`}>{s.text}</span>
    )
  );
}

function renderArticleBlock(block, idx) {
  if (block.type === 'hr') {
    return <hr key={idx} className="blog-article-rule" aria-hidden="true" />;
  }
  if (block.type === 'sig') {
    return (
      <p key={idx} className="blog-article-signature">
        {block.text}
      </p>
    );
  }
  if (block.type === 'pullQuoteWrap') {
    const quote = typeof block.quoteParagraph === 'string' ? block.quoteParagraph : '';
    const bodyParas = Array.isArray(block.bodyParagraphs) ? block.bodyParagraphs : [];
    const quoteClass =
      block.quoteAlign === 'left' ? 'blog-pull-quote blog-pull-quote--left' : 'blog-pull-quote';
    return (
      <div key={idx} className="blog-pull-quote-wrap">
        <blockquote className={quoteClass}>
          <p>{quote}</p>
        </blockquote>
        {bodyParas.map((bp, j) => (
          <p key={`${idx}-pq-${j}`}>{renderParagraphSegments(bp.segments, `${idx}-pq-${j}`)}</p>
        ))}
      </div>
    );
  }
  return (
    <p key={idx}>
      {renderParagraphSegments(block.segments, `${idx}`)}
    </p>
  );
}

/**
 * Shared layout for all journal posts (CRA + React Router — Scenario B).
 *
 * Pull image + caption: `post.pullImageSrc` etc. from {@link ./postsMeta.js}
 * Author card: resolved via `post.authorId` → {@link ./authors.js}
 *
 * Pull quote (floated): `{ type: 'pullQuoteWrap', quoteParagraph, bodyParagraphs, quoteAlign?: 'left' }`
 * — `quoteAlign: 'left'` adds `.blog-pull-quote--left` (float left). Default is right-floated.
 */
export function BlogPostLayout({ post, author, heading, blocks }) {
  useEffect(() => {
    const prev = document.title;
    document.title = post.documentTitle;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : '';
    const excerpt = String(post.metaDescription || '').trim().slice(0, 160);
    if (meta) meta.setAttribute('content', excerpt);

    const cleanupSocial = applyBlogPostSocialMeta(post, author);

    return () => {
      document.title = prev;
      if (meta) meta.setAttribute('content', prevDesc ?? '');
      cleanupSocial();
    };
  }, [post, author]);

  const pullSrc = typeof post.pullImageSrc === 'string' ? post.pullImageSrc.trim() : '';
  const showPull = Boolean(pullSrc);

  return (
    <div className="blog-page blog-article-page">
      <style>{BLOG_POST_LAYOUT_CSS}</style>
      <LandingNav positionMode="sticky" />

      <article className="blog-article-inner">
        <Link to="/blog" className="blog-article-back">
          ← Back to journal
        </Link>

        <h1 className="blog-article-title serif">{heading}</h1>

        <div className="article-byline">
          <Link to={author.aboutPath} className="article-byline__avatar-link">
            <img
              src={author.bylineAvatarSrc}
              alt={author.name}
              className="article-byline__avatar"
              width={48}
              height={48}
            />
          </Link>
          <div className="article-byline__meta">
            <Link to={author.aboutPath} className="article-byline__name">
              {author.name}
            </Link>
            <div className="article-byline__role">
              {author.bylineRolePrefix} · {post.dateLine}
            </div>
          </div>
        </div>

        <div className="blog-article-body">
          {showPull ? (
            <figure className="article-pull-image">
              <img src={pullSrc} alt={post.pullImageAlt || ''} />
              <figcaption>{post.pullImageCaption}</figcaption>
            </figure>
          ) : null}
          {blocks.map((block, idx) => renderArticleBlock(block, idx))}
        </div>

        <aside className="author-bio" aria-label="About the author">
          <Link to={author.aboutPath} className="author-bio__photo-link">
            <img
              src={author.bioPhotoSrc}
              alt={author.name}
              className="author-bio__photo"
              width={120}
              height={120}
            />
          </Link>
          <div className="author-bio__content">
            <div className="author-bio__eyebrow">Written by</div>
            <h3 className="author-bio__name">
              <Link to={author.aboutPath}>{author.name}</Link>
            </h3>
            <div className="author-bio__role">{author.bioRoleLabel}</div>
            <p
              className={
                author.bioLinkLabel && String(author.bioLinkLabel).trim()
                  ? 'author-bio__text'
                  : 'author-bio__text author-bio__text--final'
              }
            >
              {author.bioParagraph}
            </p>
            {author.bioLinkLabel && String(author.bioLinkLabel).trim() ? (
              <Link to={author.aboutPath} className="author-bio__link">
                {author.bioLinkLabel}
              </Link>
            ) : null}
          </div>
        </aside>
      </article>

      <SiteFooter />
    </div>
  );
}
